import { useState, useEffect, useRef, useCallback } from "react";
import { PaperPlaneRight, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export const MessageList = ({ messages, mineIs }) => {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  return (
    <div className="flex-1 overflow-y-auto space-y-3 p-4" data-testid="message-list">
      {messages.length === 0 && <p className="text-[var(--muted)] text-sm text-center py-10">No messages yet. Say hi!</p>}
      {messages.map((m) => {
        const mine = m.sender === mineIs;
        return (
          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-[16px] px-4 py-2.5 text-sm ${mine ? "bg-[var(--glow)] text-white rounded-br-[4px]" : "surface rounded-bl-[4px]"}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              <p className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-[var(--muted)]"}`}>{dayjs(m.createdAt).format("MMM D, HH:mm")}</p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};

export default function Contact() {
  useSeo("Contact Us", "Have a question or feature request? Message the VidhFlow team directly.");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(() => api.get("/contact/thread").then((r) => setMessages(r.data.messages)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/contact/thread", { text });
      setText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="contact-page">
      <div className="fade-up mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">We reply personally</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Contact Us</h1>
        <p className="text-[var(--muted)] text-base mt-3">Questions, bugs, feature ideas — drop a message and check back here for the reply.</p>
      </div>
      <div className="surface rounded-[20px] flex flex-col h-[480px] fade-up-1">
        <div className="px-4 py-3 border-b border-[var(--border-c)] flex items-center gap-2">
          <ChatCircleDots size={18} color="var(--glow)" weight="duotone" />
          <span className="text-sm font-semibold">VidhFlow Support</span>
        </div>
        <MessageList messages={messages} mineIs="user" />
        <form onSubmit={send} className="p-3 border-t border-[var(--border-c)] flex gap-2">
          <input
            data-testid="contact-message-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button data-testid="contact-send-btn" className="w-10 h-10 rounded-full bg-[var(--glow)] text-white flex items-center justify-center hover:bg-[#6a4ce6] transition-colors">
            <PaperPlaneRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
